import { createContext, useContext, useEffect, useState } from "react";
import { api, setAuthToken } from "@/lib/api";

const TOKEN_KEY = "crash_auth_token";
const AuthCtx = createContext({ token: null, authed: false, login: async () => {}, logout: () => {} });

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    setAuthToken(token);
    let cancelled = false;
    (async () => {
      if (!token) {
        if (!cancelled) {
          setAuthed(false);
          setChecking(false);
        }
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setAuthed(!!data?.authenticated);
      } catch (err) {
        // Only an explicit 401 means the session is invalid. A network
        // error / 5xx just means the server is busy or restarting — keep
        // the user signed in rather than kicking them to the gate while a
        // teammate's batch is processing.
        if (!cancelled) setAuthed(err?.response?.status !== 401);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = async (password) => {
    const { data } = await api.post("/auth/login", { password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setAuthToken(data.token);
    setAuthed(true);
    return data;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setAuthToken(null);
    setAuthed(false);
  };

  return (
    <AuthCtx.Provider value={{ token, authed, checking, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
